/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Card } from 'src/core/components/Card';
import { LineChart } from 'src/core/components/LineChart';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getStylist } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { AnalyticsDropdown } from 'src/luis/projects/analytics/components/TrainingEvaluation/AnalyticsDropdown';

export type EndpointHitsCardProps = {
    /**
     * The displayed endpoint hits with the key of the map is the date
     * and the value is the number of the endpoint hits in this date.
     */
    endpointHits: Map<string, number>;

    /**
     * A callback to be invoked whenever the duration of the history
     * of the endpoint hits changes.
     */
    onHistoryDurationChanged?: (duration: number) => void;
};

const messages = defineMessages({
    title: {
        id: 'analytics.endpointHitsCard.title',
        defaultMessage: 'Endpoint hits per day'
    },
    pastWeek: {
        id: 'analytics.endpointHitsCard.pastWeek',
        defaultMessage: 'Past week'
    },
    pastMonth: {
        id: 'analytics.endpointHitsCard.pastMonth',
        defaultMessage: 'Past month'
    },
    pastThreeMonths: {
        id: 'analytics.endpointHitsCard.pastThreeMonths',
        defaultMessage: 'Past 3 months'
    },
    pastSixMonths: {
        id: 'analytics.endpointHitsCard.pastSixMonths',
        defaultMessage: 'Past 6 months'
    },
    pastYear: {
        id: 'analytics.endpointHitsCard.pastYear',
        defaultMessage: 'Past year'
    },
    showResultsLabel: {
        id: 'analytics.endpointHitsCard.showResultsLabel',
        defaultMessage: 'Show results from'
    }
});

const { styleComponent } = getStylist('Dashboard');

const StyledLabel = styleComponent<ILabelProps>(Label)('StyledLabel', theme => ({
    fontSize: theme.labelFontSize,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing
}));

/**
 * The Card contains:
 * 1. The endpoint hits representing line chart.
 * 2. The associated duration filter which filters the presented hits across week, month, 3 months, 6 months or year.
 */
export const EndpointHitsCard = (props: EndpointHitsCardProps) => {
    const { endpointHits, onHistoryDurationChanged } = props;
    const { localize } = useLocalization();
    const { isXXLargeOrGreater } = useResponsiveMode();
    const eventLoggerRef = React.useRef(getEventLogger('EndpointHitsCard'));

    const xData: ChartAxisData<string> = {
        label: 'Days',
        data: Array.from(endpointHits.keys()).map(k => new Date(k).toISOString())
    };

    const yData: ChartAxisData<number> = {
        label: 'Endpoint Hits',
        data: Array.from(endpointHits.values())
    };

    const durationOptions: IDropdownOption[] = [
        { key: '7', text: localize(messages.pastWeek), data: 7, selected: true },
        { key: '31', text: localize(messages.pastMonth), data: 31 },
        { key: '92', text: localize(messages.pastThreeMonths), data: 92 },
        { key: '183', text: localize(messages.pastSixMonths), data: 183 },
        { key: '365', text: localize(messages.pastYear), data: 365 }
    ];

    React.useEffect(() => onHistoryDurationChanged(7), []);

    const onHistoryDurationSelectionChanged = (option: IDropdownOption) => {
        eventLoggerRef.current.log('HistoryDurationDropdownSelectionChange');
        onHistoryDurationChanged(option.data as number);
    };

    return (
        <Card title={localize(messages.title)}>
            <Stack verticalFill tokens={{ childrenGap: 15 }}>
                <StackItem grow={1}>
                    <LineChart xData={xData} yData={yData} xAxisType="timeseries" />
                </StackItem>

                <StackItem>
                    <Stack
                        verticalAlign="center"
                        tokens={{ childrenGap: 16 }}
                        horizontal={isXXLargeOrGreater}
                        horizontalAlign="space-between">
                        <StyledLabel>{localize(messages.showResultsLabel)}</StyledLabel>

                        <AnalyticsDropdown
                            isSearchEnabled={false}
                            options={durationOptions}
                            ariaLabel={localize(messages.showResultsLabel)}
                            onRenderCaretDown={() => <Icon iconName="CaretDownSolid8" />}
                            onChange={(_, option) => onHistoryDurationSelectionChanged(option)}
                        />
                    </Stack>
                </StackItem>
            </Stack>
        </Card>
    );
};
